import cn from 'classnames';
import React from 'react';
import styles from './cookies.module.scss';

const Section = ({ children }: { children: React.ReactNode }) => <section>{children}</section>;
Section.Title = function SectionTitle({ children }: { children: React.ReactNode }) {
  return <div className="section-title">{children}</div>;
};
Section.Paragraph = function SectionParagraph({ children }: { children: React.ReactNode }) {
  return <p className="paragraph">{children}</p>;
};
const ListItem = {
  Title: function ListItemTitle({ children }: { children: React.ReactNode }) {
    return <div>{children}</div>;
  },
  Step: function ListItemStep({ children }: { children: React.ReactNode }) {
    return <div>{children}</div>;
  },
};
const Highlight = ({ children }: { children: React.ReactNode }) => (
  <span className="font-bold">{children}</span>
);

const cookiesData = [
  {
    cookie: 'PHPSESSID',
    domain: 'securetradeclub.com',
    type: 'Necessary',
    description:
      "This cookie is native to PHP applications. The cookie is used to store and identify a users' unique session ID for the purpose of managing user session on the website. The cookie is a session cookies and is deleted when all the browser windows are closed.",
    duration: 'session',
  },
  {
    cookie: '__stripe_mid',
    domain: '.securetradeclub.com',
    type: 'Necessary',
    description:
      'This cookie is set by Stripe payment gateway. This cookie is used to enable payment on the website without storing any payment information on a server.',
    duration: '1 year',
  },
  {
    cookie: '__stripe_sid',
    domain: '.securetradeclub.com',
    type: 'Necessary',
    description:
      'This cookie is set by Stripe payment gateway. This cookie is used to enable payment on the website without storing any payment information on a server.',
    duration: '30 minutes',
  },
  {
    cookie: 'm',
    domain: 'm.stripe.com',
    type: 'Other',
    description: 'No description available.',
    duration: '2 years',
  },
];

export const Cookies = () => {
  return (
    <main className={cn('container text-left', styles.container)}>
      <h1 className="main-title">Cookie Policy</h1>
      <Section>
        <Section.Paragraph>
          This cookie policy applies to www.securetradeclub.com operated by Secure Trade Club a
          company incorporated in Florida with seat in Volusia County (“we”, “our”or “us”) acting as
          data controller.
        </Section.Paragraph>
      </Section>
      <Section>
        <Section.Title>Use of cookies</Section.Title>
        <Section.Paragraph>
          Cookies and tracking mechanisms may be used as part of the provision of our online
          services. Cookies are small text files that can be stored on your end device when you
          visit an online offer. Tracking is possible using various technologies. We process
          information in particular within the framework of pixel technology or log file analysis.
        </Section.Paragraph>
      </Section>
      <Section>
        <Section.Title>Categories</Section.Title>
        <Section.Paragraph>
          We distinguish between cookies that are absolutely necessary for the technical functions
          of the online offer and such cookies and tracking mechanisms that are not absolutely
          necessary for the technical function of the online offers and cookies that require
          consent.
        </Section.Paragraph>
        <ul>
          <li>Technical cookies</li>
          <li>Functional and performance cookies</li>
          <li>Cookies requiring consent</li>
        </ul>
        <Section.Paragraph>
          We will inform you which cookie types are set and used in each case for the tools we use.
        </Section.Paragraph>
      </Section>
      <Section>
        <Section.Title>Technical cookies</Section.Title>
        <Section.Paragraph>
          Technical cookies ensure functions without which you cannot use our websites as intended.
          These cookies are used exclusively by us and are therefore first party cookies. This means
          that all information stored in the cookies is returned to our website.
        </Section.Paragraph>
        <Section.Paragraph>
          Technical cookies are used, for example, to ensure that you, as a logged-in user, always
          remain logged in when accessing different sub-pages of our website and thus do not have to
          re-enter your login data each time you call up a new page.
        </Section.Paragraph>
        <Section.Paragraph>
          The use of technical cookies on our website is possible without your consent. For this
          reason, technical cookies cannot be individually deactivated or activated. However, you
          have the option to generally deactivate cookies in your browser at any time (see below).
        </Section.Paragraph>
      </Section>
      <Section>
        <Section.Title>Functional and performance cookies</Section.Title>
        <Section.Paragraph>
          Functional cookies enable our website to store information you have already provided (such
          as registered name or language selection) and to offer you improved and more personalised
          functions based on this information. These cookies collect and store only pseudonymous
          information, so they cannot track your movements on other websites.
        </Section.Paragraph>
        <Section.Paragraph>
          Performance cookies collect information about how our websites are used in order to
          improve their attractiveness, content and functionality.
        </Section.Paragraph>
        <Section.Paragraph>
          These cookies help us to determine, for example, whether and which subpages of our website
          are visited and which content users are particularly interested in. In particular, we
          record the number of times a page is accessed, the number of sub-pages accessed, the time
          spent on our website, the order of the pages visited, which search terms led you to us,
          the country, region and, if applicable, the city from which access is made, as well as the
          proportion of mobile devices accessing our websites.
        </Section.Paragraph>
        <Section.Paragraph>
          Furthermore, we record movements, "clicks" and scrolling with the computer mouse in order
          to understand which areas of our website are of particular interest to users. As a result,
          we can tailor the content of our website more specifically to the needs of our users and
          optimise our offering. The IP address of your computer transmitted for technical reasons
          is automatically anonymised and does not allow us to draw any conclusions about the
          individual user.
        </Section.Paragraph>
        <Section.Paragraph>
          The functional and performance cookies are so-called "absolutely necessary" cookies as
          defined by the ePrivacy Directive 2002/58 EC, which do not require consent.
        </Section.Paragraph>
      </Section>
      <Section>
        <Section.Title>Cookies requiring consent</Section.Title>
        <Section.Paragraph>
          Cookies that are neither technical cookies nor functional or performance cookies will only
          be used with your consent.
        </Section.Paragraph>
        <Section.Paragraph>
          We reserve the right to also use information obtained by means of cookies from an analysis
          of the usage behaviour of visitors to our websites in order to display specific
          advertising for certain of our products to you on our own websites. We believe that this
          benefits you as a user because we display advertising or content that we believe, based on
          your browsing behaviour, matches your interests and so you are less likely to be shown
          random advertising or specific content that may be of less interest to you.
        </Section.Paragraph>
        <Section.Paragraph>
          Marketing cookies come from external companies (third party cookies) and are used to
          collect information across websites visited by the user in order to create targeted
          advertisements for the user. These enable personalised online ads and advanced analysis
          and evaluation options about the target group and user behaviour.
        </Section.Paragraph>
      </Section>
      <Section>
        <Section.Title>Opt-out for marketing cookies</Section.Title>
        <Section.Paragraph>
          You can also manage cookies used for online advertising through tools developed in many
          countries as part of self-regulatory programmes, such as the US-based{' '}
          <a href="https://www.aboutads.info/choices">https://www.aboutads.info/choices</a> or the
          EU-based{' '}
          <a href="http://www.youronlinechoices.com/uk/your-ad-choices">
            http://www.youronlinechoices.com/uk/your-ad-choices
          </a>
          .
        </Section.Paragraph>
      </Section>
      <Section>
        <Section.Title>Management and deletion of all cookies</Section.Title>
        <Section.Paragraph>
          In addition, you can set your Internet browser in such a way that cookies are generally
          prevented from being saved on your end device or you are asked each time whether you agree
          to cookies being set. Once cookies have been set, you can also delete them at any time.
          You can find out how all this works in detail in the help function of your browser.
        </Section.Paragraph>
        <ul>
          <li>
            <ListItem.Title>
              Disabling cookies in <Highlight>Internet Explorer</Highlight>
            </ListItem.Title>
            <ListItem.Step>Select the "Tools" menu and then "Internet Options".</ListItem.Step>
            <ListItem.Step>Click on the "Privacy" tab.</ListItem.Step>
            <ListItem.Step>Select the appropriate setting.</ListItem.Step>
          </li>
          <li>
            <ListItem.Title>
              Disabling cookies in <Highlight>Google Chrome</Highlight>
            </ListItem.Title>
            <ListItem.Step>Click Settings {'>'} Advanced.</ListItem.Step>
            <ListItem.Step>Under Security and Privacy, select Content Settings.</ListItem.Step>
            <ListItem.Step>Click on "Cookies".</ListItem.Step>
          </li>
          <li>
            <ListItem.Title>
              Disabling cookies in <Highlight>Safari</Highlight>
            </ListItem.Title>
            <ListItem.Step>Go to Preferences {'>'} Privacy.</ListItem.Step>
            <ListItem.Step>Click on "Remove all website data".</ListItem.Step>
          </li>
          <li>
            <ListItem.Title>
              Disabling cookies in <Highlight>Firefox</Highlight>
            </ListItem.Title>
            <ListItem.Step>Select the "Tools" menu and then "Options".</ListItem.Step>
            <ListItem.Step>Click on the "Privacy" icon.</ListItem.Step>
            <ListItem.Step>Click on the "Cookie" menu and select the desired options</ListItem.Step>
          </li>
          <li>
            <ListItem.Title>
              Disabling cookies in <Highlight>Opera</Highlight>
            </ListItem.Title>
            <ListItem.Step>Opera 6.0 and later versions.</ListItem.Step>
            <ListItem.Step>Go to the "File" {'>'} "Preferences" menu.</ListItem.Step>
          </li>
        </ul>
        <Section.Paragraph>
          You can also delete Cookies following the links embedded below
        </Section.Paragraph>
        <ul>
          <li>
            Delete cookies in{' '}
            <a href="https://support.google.com/chrome/answer/95647?hl=en-GB&co=GENIE.Platform%3DDesktop">
              Google Chrome
            </a>
          </li>
          <li>
            Delete cookies in{' '}
            <a href="https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox">
              Mozilla Firefox
            </a>
          </li>
          <li>
            Deleting <a href="https://www.wikihow.com/Delete-Flash-Cookies">Flash cookies</a>
          </li>
          <li>
            Delete cookies in{' '}
            <a href="https://support.microsoft.com/en-us/windows/delete-and-manage-cookies-168dab11-0753-043d-7c16-ede5947fc64d">
              Microsoft Internet Explorer
            </a>
          </li>
          <li>
            Delete cookies in{' '}
            <a href="https://www.opera.com/use-cases/clean-browser-and-remove-trackers">Opera</a>
          </li>
          <li>
            Delete cookies in <a href="https://support.apple.com/en-gb/HT201265">Safari</a>
          </li>
        </ul>
      </Section>
      <Section>
        <Section.Title>The cookies we use</Section.Title>
        <Section.Paragraph>The list below shows the cookies we have installed.</Section.Paragraph>
        <table className="hidden sm:block">
          <tr>
            <th>Cookie</th>
            <th>Domain</th>
            <th>Type</th>
            <th>Description</th>
            <th>Duration</th>
          </tr>
          {cookiesData.map((cookie) => (
            <tr key={cookie.cookie}>
              <td>{cookie.cookie}</td>
              <td>{cookie.domain}</td>
              <td>{cookie.type}</td>
              <td>{cookie.description}</td>
              <td>{cookie.duration}</td>
            </tr>
          ))}
        </table>
        <ul className="block sm:hidden">
          {cookiesData.map((cookie, index) => (
            <li key={index}>
              <ListItem.Title>
                <Highlight>Cookie</Highlight>: {cookie.cookie}
              </ListItem.Title>
              <ListItem.Step>
                <Highlight>Domain</Highlight>: {cookie.domain}
              </ListItem.Step>
              <ListItem.Step>
                <Highlight>Type</Highlight>: {cookie.type}
              </ListItem.Step>
              <ListItem.Step>
                <Highlight>Description</Highlight>: {cookie.description}
              </ListItem.Step>
              <ListItem.Step>
                <Highlight>Duration</Highlight>: {cookie.duration}
              </ListItem.Step>
            </li>
          ))}
        </ul>
      </Section>
      <Section>
        <Section.Title>What about Google Chrome cookies?</Section.Title>
        <Section.Paragraph>
          Separately to our use of Google Analytics cookies, when you access the Site via Google
          applications such as Google Chrome, Google may collect user information for the purpose of
          Google serving personalised advertising across your devices. Google is able place these
          advertising cookies on the devices used by visitors to the Site because we use Google
          services to run the Site. However these cookies are dropped for Google’s own purposes and
          as such we do not have any control over how or when they are dropped. You will need to opt
          out of receiving these cookies via your Google settings. For further information about
          these cookies please see:{' '}
          <a href="https://policies.google.com/technologies/types?hl=en-US">
            https://policies.google.com/technologies/types?hl=en-US
          </a>
          . Please note that other browsers may use similar tools.
        </Section.Paragraph>
      </Section>
      <Section>
        <Section.Title>Does this policy change?</Section.Title>
        <Section.Paragraph>
          We may update our Cookie Policy from time to time. This might be for a number of reasons,
          such as to reflect a change in the law or to accommodate a change in our business
          practices and the way we use cookies. We recommend that you check here periodically for
          any changes to our Cookie Policy.
        </Section.Paragraph>
      </Section>
    </main>
  );
};
